export function debounce(func: any, wait: number) {
  let timeout: NodeJS.Timeout;

  return (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number,
  options: { leading?: boolean; trailing?: boolean } = {
    leading: true,
    trailing: false,
  }
): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: any[] | null = null;
  let lastContext: any = null;
  let lastCallTime: number | null = null;

  const invokeFunction = () => {
    if (lastArgs !== null && lastContext !== null) {
      func.apply(lastContext, lastArgs);
      lastCallTime = Date.now();
      lastArgs = null;
      lastContext = null;
      timeout = null;
    }
  };

  return function (this: any, ...args: any[]): void {
    const now = Date.now();
    const shouldInvoke = options.leading && lastCallTime === null;

    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastContext = this;

    if (lastCallTime !== null && now - lastCallTime >= limit) {
      invokeFunction();
    }

    if (timeout === null) {
      timeout = setTimeout(
        () => {
          if (options.trailing && lastArgs !== null) {
            invokeFunction();
          }
        },
        shouldInvoke ? limit - (now - (lastCallTime ?? now)) : limit
      );
    }

    if (shouldInvoke) {
      invokeFunction();
    }
  } as T;
}

const slugify = (value: unknown): string => {
  if (typeof value !== 'string') {
    console.warn('slugify expected string value but received:', typeof value, value);

    return '';
  }

  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

export const datasetToAttributes = (dataset: Record<string, string> | undefined) => {
  if (!dataset) {
    return {};
  }

  return Object.entries(dataset).reduce((acc, [key, value]) => {
    if (typeof value !== 'string') {
      console.warn(
        `datasetToAttributes expected string value for key "${key}" but received:`,
        typeof value,
        value
      );

      acc[`data-${key}`] = '';
      return acc;
    }

    /* Disabling this logic temporary to avoid breaks on automated tests
     * @danieltheaxe needs to be updated when this logic were reapplied
     * Make sure to re-enable unit tests related when this logic is reapplied
     */
    // acc[`data-${key}`] = slugify(value);
    acc[`data-${key}`] = value;
    return acc;
  }, {} as Record<string, string>);
};

export function capitalizeFirstLetter(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function findDefaultTabIndex(children: any[], tabPriority?: string[]): number {
  if (!tabPriority || !Array.isArray(children)) {
    return 0;
  }

  // Find the first available tab from the priority list
  for (const priorityType of tabPriority) {
    const tabIndex = children.findIndex((child) => (child.type || child.id) === priorityType);

    if (tabIndex !== -1) {
      // Check if the tab is not hidden
      const isHidden = children[tabIndex].hide && children[tabIndex].hide();

      if (!isHidden) {
        return tabIndex;
      }
    }
  }

  // If no priority tab is available, return the first non-hidden tab
  for (let i = 0; i < children.length; i++) {
    const isHidden = children[i].hide && children[i].hide();

    if (!isHidden) {
      return i;
    }
  }

  return 0;
}
